import React, { useState } from 'react';
import { getAffiliateByCode, getAffiliateCommissions } from '../../services/affiliate';
import { formatCurrency } from '../../utils/format';
import '../../styles/affiliate.css';

const AffiliateDashboard = () => {
  const [affiliateCode, setAffiliateCode] = useState('');
  const [affiliateData, setAffiliateData] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [codeEntered, setCodeEntered] = useState(false);

  const handleLookupAffiliate = async (e) => {
    e.preventDefault();
    if (!affiliateCode.trim()) {
      setError('Please enter an affiliate code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get affiliate data by code
      const affiliate = await getAffiliateByCode(affiliateCode);
      if (!affiliate) {
        setError('Invalid affiliate code');
        return;
      }

      setAffiliateData(affiliate);
      setCodeEntered(true);

      // Get commission history
      const commissionData = await getAffiliateCommissions(affiliate.id);
      setCommissions(commissionData);
    } catch (error) {
      console.error('Error loading affiliate data:', error);
      setError('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAffiliateCode('');
    setAffiliateData(null);
    setCommissions([]);
    setError('');
    setCodeEntered(false);
  };

  if (!codeEntered) {
    return (
      <div className="affiliate-dashboard">
        <div className="page-header">
          <h1>Affiliate Dashboard</h1>
          <p>Enter your affiliate code to view your earnings</p>
        </div>

        <div className="code-lookup-form">
          <form onSubmit={handleLookupAffiliate}>
            <div className="form-group">
              <label htmlFor="affiliateCode">Affiliate Code</label>
              <input
                type="text"
                id="affiliateCode"
                value={affiliateCode}
                onChange={(e) => setAffiliateCode(e.target.value.toUpperCase())}
                placeholder="Enter your affiliate code"
                style={{ textTransform: 'uppercase' }}
                required
              />
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Loading...' : 'View Dashboard'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const pendingEarnings = commissions
    .filter(c => c.status === 'pending')
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  const paidEarnings = commissions
    .filter(c => c.status === 'paid')
    .reduce((sum, c) => sum + c.commissionAmount, 0);

  return (
    <div className="affiliate-dashboard">
      <div className="page-header">
        <h1>Affiliate Dashboard</h1>
        <div className="header-actions">
          <p>Track your referral earnings</p>
          <button className="btn btn-secondary" onClick={handleReset}>
            Enter Different Code
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="affiliate-stats">
        <div className="stat-card">
          <h3>Total Referrals</h3>
          <div className="stat-value">{affiliateData.totalReferrals || 0}</div>
        </div>
        
        <div className="stat-card">
          <h3>Total Earnings</h3>
          <div className="stat-value">{formatCurrency(affiliateData.totalEarnings || 0)}</div>
        </div>
        
        <div className="stat-card">
          <h3>Pending Earnings</h3>
          <div className="stat-value">{formatCurrency(pendingEarnings)}</div>
        </div>
        
        <div className="stat-card">
          <h3>Paid Earnings</h3>
          <div className="stat-value">{formatCurrency(paidEarnings)}</div>
        </div>
      </div>

      {/* Commission History */}
      <div className="commissions-section">
        <h2>Commission History</h2>
        
        {commissions.length === 0 ? (
          <div className="no-commissions">
            <p>No commissions yet. Start referring users to earn!</p>
          </div>
        ) : (
          <div className="commissions-list">
            {commissions.map((commission) => (
              <div key={commission.id} className="commission-item">
                <div className="commission-info">
                  <h4>Referral Payment</h4>
                  <p className="commission-details">
                    Original: {formatCurrency(commission.originalAmount)} | 
                    Discount: {formatCurrency(commission.discountAmount)} | 
                    Final: {formatCurrency(commission.finalAmount)}
                  </p>
                  <p className="commission-date">
                    {new Date(commission.createdAt).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="commission-amount">
                  <div className="amount">{formatCurrency(commission.commissionAmount)}</div>
                  <div className={`status ${commission.status}`}>
                    {commission.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Affiliate Code Info */}
      <div className="affiliate-code-section">
        <h2>Your Affiliate Code</h2>
        <div className="code-display">
          <span className="code">{affiliateData.code}</span>
          <span className="discount-info">20% discount for users</span>
        </div>
        <p>Share this code with users to earn 15% commission on their payments.</p>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
